import React from 'react';
import { Leaf, Recycle, Battery, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { industries, materials } from '../mockData';

const iconMap = {
  Leaf: Leaf,
  Recycle: Recycle,
  Battery: Battery
};

const Collaboration = () => {
  return (
    <section id="collaboration" className="py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Industry Collaboration
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Partnering with leading industries to create sustainable solutions for energy and environmental challenges
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {industries.map((industry, index) => {
            const Icon = iconMap[industry.icon];
            return (
              <Card
                key={index}
                className="group border-2 hover:border-green-600 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-blue-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl mb-3">{industry.name}</CardTitle>
                  <CardDescription className="text-base text-gray-600 mb-4">
                    {industry.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Applications:</div>
                    {industry.applications.map((app, idx) => (
                      <div key={idx} className="flex items-center text-sm text-gray-600">
                        <ArrowRight className="w-4 h-4 text-green-600 mr-2" />
                        {app}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Materials Section */}
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-8 lg:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Materials We Process
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {materials.map((material, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border-2 border-transparent hover:border-green-600 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-lg font-bold text-gray-900">{material.name}</h4>
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                    material.category === 'Biomass' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {material.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{material.description}</p>
              </div>
            ))}
          </div>
          
          {/* Additional Materials Note */}
          <div className="bg-white rounded-xl p-6 border-2 border-blue-300">
            <p className="text-base text-gray-700 leading-relaxed">
              <strong className="font-semibold text-gray-900">Other materials</strong> such as ceramics and additional advanced materials are also supported for thermal treatment within <strong>600–3,000 °C</strong>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Collaboration;
